import React, { useRef, useState, useEffect } from 'react';
import axios, { AxiosError } from 'axios';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import toast from '../../../utils/toast';
import {
  StripeCardPanel,
  StripeIntent,
  StripeCardConfirmation,
  preloadStripe,
} from '../../payments/stripe-card-modal';
import { LogoMark } from '../../logo/logo-mark';
import { RightArrow } from '../../icons/right-arrow';
import { LeftArrow } from '../../icons/left-arrow';
import { useAuth } from '../../../context/auth-provider';
import { publicApi } from '../../../api/public-client';
import { apiRoute } from '../../../constants/api-constants';
import { resolvePostAuthRoute } from '../../../utils/post-auth-route';
import { PlanPicker, Plan } from '../plan-picker';
import { OnboardingProgress } from '../onboarding-progress';
import { CityAutocomplete, CityPoint } from './city-autocomplete';
import {
  BuildingLocation,
  BuildingLocationPicker,
  EMPTY_LOCATION,
} from '../../pages/dashboard/dashboard-buildings-components/building-location-picker';
import {
  GeoAccessContext,
  PUBLIC_GEO,
} from '../../pages/dashboard/dashboard-buildings-components/geo-access';
import { deviceZone } from '../../../function-utils/building-time';
import { Onboarding } from '../../../interfaces/onboarding-interfaces';
import { User } from '../../../interfaces/user-interfaces';
import {
  AutoHeight,
  Collapse,
  Pop,
  Shake,
  StatusLabel,
  StepDirection,
  StepTransition,
  preset,
} from '../../../motion';
import {
  Shell,
  Card,
  BrandRow,
  Wordmark,
  Heading,
  Subheading,
  Field,
  FieldRow,
  Label,
  Input,
  Select,
  FullWidthButton,
  PrimaryButton,
  SecondaryButton,
  ButtonRow,
  STEP_LAYER_STYLE,
  SuccessRow,
  FieldError,
  FieldHint,
  HelperText,
  TopLink,
  SummaryBox,
  SummaryRow,
  SummaryValue,
  SummaryTotal,
  SuccessBlock,
  SuccessIcon,
  InfoBanner,
} from '../onboarding.styled';

// The 5-step admin onboarding wizard — the FIRST onboarding. Source of truth:
// lobbero-landing-green.html (its signup modal). plan → building → account →
// payment (ONE screen, real Stripe split Elements) → success. The only deviation
// from the mockup is the added confirm-password field. After "Done", the user
// lands in the SECOND onboarding (add managers / buildings / units / tenants).
type StepKey = 'plan' | 'building' | 'account' | 'payment' | 'done';
const ORDER: StepKey[] = ['plan', 'building', 'account', 'payment'];
// The same wizard with the payment step removed (FREE_SIGNUP). The server is
// what actually decides whether a signup costs anything — this only controls
// whether we ask for a card we know will not be used. See the note on
// `freeSignup` below.
const FREE_ORDER: StepKey[] = ['plan', 'building', 'account'];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// The success screen's three lines arrive in a short cascade (index-delayed
// listItem variants, no layout animation: nothing here ever reorders).
const arrive = (index: number) => ({
  custom: index,
  variants: preset.listItem,
  initial: 'hidden' as const,
  animate: 'visible' as const,
});

// The publishable key, `loadStripe`, the split card Elements, CARD_ELEMENT_STYLE,
// STRIPE_FONTS and the confirm-with-retry logic all moved to
// `components/payments/stripe-card-modal.tsx` so the dashboard can use the same
// surface. This file no longer imports Stripe at all — it supplies the signup
// STEPS (POST /signup, the promo code, the success hand-off) and nothing else.

interface StaffingOption {
  code: string;
  label: string;
}

const PERSONAS = [
  { value: 'property_manager', label: 'Property manager' },
  { value: 'board_member', label: 'Board member' },
  { value: 'concierge_company', label: 'Concierge company' },
  { value: 'building_owner', label: 'Building owner' },
  { value: 'other', label: 'Other' },
];

// ---------------------------------------------------------------------------
// EVERY FIGURE ON THIS PAGE COMES FROM plans.price_cents (owner decision D-12)
// ---------------------------------------------------------------------------
// There used to be a `PLAN_FLOOR = { desk: 99, desk_iq: 179 }` here and the
// summary quoted `Math.max(rate * units, floor)`. Per-unit pricing is the truth
// and there is no minimum charge anywhere in Stripe, so that floor quoted a
// number nobody would ever be invoiced: a 10-unit Desk org pays $12.50/month,
// not $99. It is DELETED rather than moved server-side, because there is no
// floor to move — see the WP-13 entry in the execution log for the list of
// signup orgs that were quoted one.
//
// Nothing here may reintroduce a rate. `plan.price_cents`, `plan.currency` and
// the unit count are the only inputs, and the same numbers are recomputed and
// re-quoted server-side by GET /api/subscriptions/units/preview once an account
// exists. Two decimals throughout: the monthly total used to round to whole
// dollars to match a mockup, which turns a real $56.25 bill into "$56".
const symbolFor = (currency?: string) =>
  (currency || 'usd').toLowerCase() === 'usd' ? '$' : `${currency} `;

const money2 = (n: number, currency?: string) =>
  `${symbolFor(currency)}${n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

interface PendingAuth {
  user: User;
  onboarding: Onboarding | null;
  permissions: string[];
}

// POST /signup answers with ONE of two shapes, and `account_created` says which
// (owner decision D-17 — no account may be created without Stripe):
//   * a real signup — user + onboarding + permissions + payment, where `payment`
//     is now GUARANTEED on a 201: the Stripe customer and the trialing
//     subscription are created BEFORE the account, so a response without a setup
//     secret is impossible rather than merely unlikely. There is no
//     `checkout_error` any more — a Stripe failure is a non-2xx with a `code`,
//     and no account exists to flag.
//   * a Custom (contact-sales) pick — a SALES LEAD and nothing else. No user, no
//     session, no workspace. `account_created: false`.
interface SignupResponse {
  user?: User;
  onboarding?: Onboarding | null;
  permissions?: string[];
  payment?: { client_secret: string; subscription_id: string } | null;
  account_created?: boolean;
  custom_requested?: boolean;
  sales_lead?: boolean;
  // TRUE when the server skipped Stripe entirely (FREE_SIGNUP). It is the
  // server saying that `payment: null` is the intended outcome, so the paid
  // path can keep treating a missing client_secret as the failure it is.
  free_signup?: boolean;
}

// ── Payment step (real Stripe, one screen) ────────────────────────────────────
// The card fields, the Elements provider and the confirmation now live in
// <StripeCardPanel>. What stays here is what is specific to SIGNUP: creating the
// account (POST /signup, which is also what mints the SetupIntent), the promo
// code, and the hand-off to the success screen. signIn stays deferred.
//
// Order of operations is unchanged: POST /signup (Stripe trial + account +
// SetupIntent) → confirmCardSetup → apply promo → parent's success screen.
interface TrialPaymentFormProps {
  planName: string;
  // The chosen row's code — sent with a promo lookup so the server can say
  // whether the code fits THIS interval (a temporary monthly promotion does
  // not apply to annual billing).
  planCode: string;
  buildingLabel: string;
  /** How many units are being bought. Sent with the promo lookup so the server
      can ask Stripe what the discount is actually worth on THIS purchase. */
  units: number;
  ratePerUnit: number;
  // Rate x units for ONE billing period: a month, or a year on an annual plan.
  periodTotal: number;
  interval: 'month' | 'year';
  currency: string;
  customerName: string;
  email: string;
  buildPayload: () => Record<string, unknown>;
  onBack: () => void;
  onEmailTaken: () => void;
  onComplete: (
    pending: PendingAuth,
    doneRoute: string,
    promoDescription: string | null
  ) => void;
}

const TrialPaymentForm: React.FC<TrialPaymentFormProps> = ({
  planName,
  planCode,
  buildingLabel,
  units,
  ratePerUnit,
  periodTotal,
  interval,
  currency,
  customerName,
  email,
  buildPayload,
  onBack,
  onEmailTaken,
  onComplete,
}) => {
  const [promo, setPromo] = useState('');
  const [promoInfo, setPromoInfo] = useState<{
    valid: boolean;
    description?: string;
    // The server's reason, when a real code does not fit this plan's
    // interval — shown verbatim instead of a generic "not valid".
    message?: string;
    // ── THE DISCOUNTED TOTAL, AND WHERE IT COMES FROM ──────────────────────
    //
    // Straight from the server, which asked STRIPE's own invoice engine against
    // the real price, the real quantity and the real coupon. This screen does
    // not multiply anything by a percentage: a discount computed in a browser
    // and an invoice raised by Stripe agree until they do not, and the day they
    // disagree is the day a customer is charged a number they never saw.
    amounts?: {
      currency: string;
      subtotal_cents: number;
      discount_cents: number;
      total_cents: number;
    } | null;
  } | null>(null);
  const [promoChecking, setPromoChecking] = useState(false);
  // Once the account + SetupIntent exist, a declined-card retry must re-confirm
  // WITHOUT re-POSTing /signup (which would 409). The CLIENT SECRET half of that
  // now lives in <StripeCardPanel> (it caches whatever `prepareIntent` returned
  // and never calls it twice); what stays here is the rest of the created state
  // — the subscription id the promo needs and the session the success screen
  // needs. The panel's `onLockChange` disables Back for the same reason: once
  // the account exists the form must not be able to unmount and lose this.
  const session = useRef<{
    subscriptionId: string;
    pendingAuth: PendingAuth;
    doneRoute: string;
  } | null>(null);

  // Explicit "Apply" — validates the code and shows the result inline. Actually
  // applying it to the subscription happens on submit (the sub must exist first).
  const applyPromo = async () => {
    const code = promo.trim();
    if (!code) {
      setPromoInfo(null);

      return;
    }
    setPromoChecking(true);
    try {
      const res = await publicApi.get('/signup/promo', {
        params: { code, plan_code: planCode, units },
      });
      setPromoInfo(res.data || { valid: false });
    } catch {
      setPromoInfo({ valid: false });
    } finally {
      setPromoChecking(false);
    }
  };

  // STEP 1 — create the Stripe trial + the account + the SetupIntent, ONCE.
  // Server order since D-17: Stripe first, then the account. So a 201 here means
  // BOTH exist; a Stripe problem arrives as a non-2xx with no account behind it
  // and is handled by `handleError` below. The panel calls this at most once and
  // caches the result, so a declined card retries the confirmation without a
  // second /signup.
  const prepareIntent = async (): Promise<StripeIntent> => {
    const res = await publicApi.post('/signup', buildPayload());
    const { user, onboarding, permissions, payment } = res.data as SignupResponse;
    if (!user) throw new Error('Signup returned no account.');
    const doneRoute = resolvePostAuthRoute(user, onboarding || null, permissions || []);
    const pending: PendingAuth = {
      user,
      onboarding: onboarding || null,
      permissions: permissions || [],
    };
    // Not a state the server can produce any more: the subscription and its
    // SetupIntent exist before the account does, so a 201 without a secret
    // would mean the account was created without Stripe — the one thing D-17
    // forbids. Surface it instead of silently starting a card-free trial,
    // which is how card-less organisations used to be born.
    if (!payment || !payment.client_secret) {
      throw new Error(
        'Signup completed without a payment setup. Please contact support.'
      );
    }
    session.current = {
      subscriptionId: payment.subscription_id,
      pendingAuth: pending,
      doneRoute,
    };

    // A TRIAL SAVES A CARD, IT DOES NOT CHARGE ONE — hence 'setup'. Sending
    // 'payment' here would make the panel call confirmCardPayment against a
    // SetupIntent secret and fail; sending 'setup' from a path that really does
    // take money would report success while nothing was paid.
    return { mode: 'setup', clientSecret: payment.client_secret };
  };

  // STEP 2 — runs only after Stripe confirmed the card.
  const onConfirmed = async (result: StripeCardConfirmation) => {
    // Assert which confirmation ran. Signup is a trial: the only correct answer
    // is confirmCardSetup. If this ever says confirmCardPayment the server
    // started handing out PaymentIntents and this screen is quietly charging
    // people on what it still calls a free trial.
    if (result.confirmed !== 'confirmCardSetup') {
      throw new Error(
        'Unexpected payment confirmation on a free trial. Nothing was completed — please contact support.'
      );
    }
    const created = session.current;
    if (!created) {
      throw new Error('Signup state was lost. Please try again.');
    }

    // Apply the promo the user validated with "Apply" (best-effort).
    // Authenticated → default axios so the CSRF header + session cookie ride
    // along. Only a code the user explicitly Applied (promoInfo.valid) is
    // sent — no async race, and an invalid code is never applied.
    let promoDescription: string | null = null;
    const code = promo.trim();
    if (code && promoInfo && promoInfo.valid) {
      try {
        const r = await axios.post(`${apiRoute}/subscriptions/apply-promo`, {
          subscription_id: created.subscriptionId,
          code,
        });
        promoDescription = (r.data && r.data.description) || null;
      } catch {
        toast('Card saved — that promo code could not be applied.', {
          icon: 'ℹ️',
        });
      }
    }

    // Into the success screen.
    onComplete(created.pendingAuth, created.doneRoute, promoDescription);
  };

  // A duplicate email is the one failure with its own destination: back to the
  // account step with the field flagged, and no card error and no toast. Every
  // other failure keeps the form exactly as the user left it — since D-17 a
  // failure here means NO ACCOUNT WAS CREATED, so the same form can simply be
  // submitted again and there is no half-finished signup to clean up.
  const handleError = (err: unknown): boolean => {
    const axiosError = err as AxiosError<{ message?: string; error?: string }>;
    if (axiosError.response?.status === 409) {
      onEmailTaken();

      return true;
    }

    return false;
  };

  // Only when the code was APPLIED and the server priced it. A valid code the
  // server could not price (no plan on the lookup, say) shows its description
  // and no figures — never a figure this screen worked out for itself.
  const promoAmounts =
    promoInfo && promoInfo.valid && promoInfo.amounts ? promoInfo.amounts : null;

  return (
    <StripeCardPanel
      collectCard
      prepareIntent={prepareIntent}
      onConfirmed={onConfirmed}
      onError={handleError}
      fallbackErrorMessage="Could not start your trial. Please try again."
      billingDetails={{ name: customerName, email }}
      summaryRows={
        // ── THE DISCOUNTED ROWS ARE THE SERVER'S NUMBERS ──────────────────
        //
        // When a promotion has been applied, two rows are ADDED under the
        // totals: what it takes off, and what the first payment after the trial
        // will be. Both come from `promoInfo.amounts`, which the server got from
        // Stripe's own invoice preview — nothing on this screen multiplies a
        // price by a percentage. The undiscounted rows stay exactly as they
        // were, so the customer can see what changed.
        //
        // The wording says "first payment" rather than "total", because a
        // 3-month or first-payment-only promotion does NOT discount every
        // invoice, and a summary that implied it did would be a promise the
        // second invoice breaks.
        interval === 'year'
          ? [
              { label: 'Plan', value: `${planName} · Annual` },
              { label: 'Building', value: buildingLabel },
              {
                label: 'Rate',
                value: `${money2(ratePerUnit, currency)}/unit/yr`,
              },
              {
                label: 'Works out to',
                // A comparison figure only (an annual plan is billed once a
                // year): whole cents, half-up, never a float's guess at x.xx5.
                value: `${money2(Math.round(Math.round(periodTotal * 100) / 12) / 100, currency)}/mo`,
              },
              {
                label: 'Billed annually (after trial)',
                value: `${money2(periodTotal, currency)}/yr`,
                total: !promoAmounts,
              },
              ...(promoAmounts
                ? [
                    {
                      label: `${promoInfo?.description} — you save`,
                      value: `-${money2(promoAmounts.discount_cents / 100, currency)}`,
                    },
                    {
                      label: 'First payment after the trial',
                      value: money2(promoAmounts.total_cents / 100, currency),
                      total: true,
                    },
                  ]
                : []),
            ]
          : [
              { label: 'Plan', value: `${planName} · Monthly` },
              { label: 'Building', value: buildingLabel },
              {
                label: 'Rate',
                value: `${money2(ratePerUnit, currency)}/unit/mo`,
              },
              {
                label: 'Per month (after trial)',
                value: `${money2(periodTotal, currency)}/mo`,
              },
              {
                label: 'Total per year',
                value: `${money2((Math.round(periodTotal * 100) * 12) / 100, currency)}/yr`,
                total: !promoAmounts,
              },
              ...(promoAmounts
                ? [
                    {
                      label: `${promoInfo?.description} — you save`,
                      value: `-${money2(promoAmounts.discount_cents / 100, currency)}`,
                    },
                    {
                      label: 'First payment after the trial',
                      value: money2(promoAmounts.total_cents / 100, currency),
                      total: true,
                    },
                  ]
                : []),
            ]
      }
      submitLabel={
        <>
          Start free trial <RightArrow />
        </>
      }
      busyLabel={
        <>
          Starting your trial… <RightArrow />
        </>
      }
      cancelLabel={
        <>
          <LeftArrow /> Back
        </>
      }
      onCancel={onBack}
      noKeyContent={
        <>
          {/* No publishable key: the card step cannot be rendered, and D-17 has
              no card-free branch to fall back to — an account is never created
              without Stripe. Say so plainly instead of offering a trial the
              server would refuse. */}
          <InfoBanner tone="warning">
            Sign-up is temporarily unavailable — our payment provider isn&rsquo;t
            reachable from this page right now. Nothing has been created, so please try
            again shortly or email hello@lobbero.com.
          </InfoBanner>
          <ButtonRow>
            <SecondaryButton type="button" onClick={onBack}>
              <LeftArrow /> Back
            </SecondaryButton>
          </ButtonRow>
        </>
      }
    >
      <Field>
        <Label htmlFor="promo">
          Promo code <span style={{ fontWeight: 400, color: '#5E7477' }}>(optional)</span>
        </Label>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <div style={{ flex: 1 }}>
            <Input
              id="promo"
              value={promo}
              onChange={e => {
                setPromo(e.target.value);
                setPromoInfo(null);
              }}
              placeholder="LISBOA26"
              autoCapitalize="characters"
              autoComplete="off"
            />
          </div>
          <SecondaryButton
            type="button"
            onClick={applyPromo}
            disabled={promoChecking || !promo.trim()}
            style={{ flex: '0 0 auto' }}
          >
            <StatusLabel
              state={promoChecking ? 'busy' : 'idle'}
              labels={{ idle: 'Apply', busy: 'Checking…' }}
            />
          </SecondaryButton>
        </div>
        <Collapse open={!!promoInfo && promoInfo.valid}>
          <HelperText style={{ color: '#4C9F38' }}>
            {promoInfo?.description} applied at checkout.
          </HelperText>
        </Collapse>
        <FieldError
          message={
            promoInfo && !promoInfo.valid
              ? promoInfo.message || 'That promo code is not valid.'
              : undefined
          }
        />
      </Field>
    </StripeCardPanel>
  );
};

// ── The wizard ────────────────────────────────────────────────────────────────
export const SignupPage: React.FC = () => {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  // The marketing site's per-plan CTAs link here as /signup?plan=desk_iq, so a
  // visitor who already chose on lobbero.com does not have to choose twice. It
  // is only a PRESELECTION — the picker is still fully editable, and an unknown
  // or absent code just falls through to the normal default.
  const [searchParams] = useSearchParams();
  const requestedPlanCode = searchParams.get('plan');
  // ?interval=year|annual|month|monthly from the same marketing link. Only a
  // preselection of which rows the picker shows; anything else is ignored.
  const rawInterval = (searchParams.get('interval') || '').toLowerCase();
  const requestedInterval: 'month' | 'year' | null =
    rawInterval === 'year' || rawInterval === 'annual'
      ? 'year'
      : rawInterval === 'month' || rawInterval === 'monthly'
        ? 'month'
        : null;

  const [step, setStep] = useState<StepKey>('plan');
  // Which way the card is travelling, so the step slides in from the side the
  // user is moving towards. Set alongside every setStep.
  const [direction, setDirection] = useState<StepDirection>('forward');
  // Counts refusals. The field shake is keyed on it so a second Continue with
  // the same field still empty shakes again instead of staying still.
  const [attempt, setAttempt] = useState(0);
  const [plan, setPlan] = useState<Plan | null>(null);
  const [staffing, setStaffing] = useState<StaffingOption[]>([]);
  // UNITS STARTS EMPTY. It used to default to '150', which at per-unit pricing
  // is a bill nobody chose: somebody who clicked through this step without
  // reading it bought 150 units. The placeholder still shows 150 as an example
  // and the running total below the field prices whatever is typed, so the
  // number is a decision now rather than an inherited one.
  const [building, setBuilding] = useState({
    name: '',
    units: '',
    city: '',
    staffing_code: '',
  });
  // Where the picked city is, for the first building's time zone. Held apart
  // from `building` on purpose: it is not part of the saved draft, and a
  // restored draft simply falls back to the browser's zone.
  const [cityPoint, setCityPoint] = useState<CityPoint | null>(null);
  // THE BUILDING'S PIN (2026-10-14): the same map the building forms use.
  // "Use my location" drops the pin where the person is; they drag it onto the
  // building. Optional — a building saved without it is pinned later from
  // Buildings. Not part of the saved draft, like the city's point.
  const [pin, setPin] = useState<BuildingLocation>({ ...EMPTY_LOCATION });
  // Typed into the map's search box but never resolved to a pin: refused, so
  // an address is never silently dropped (the building forms' rule).
  const [pinUnresolved, setPinUnresolved] = useState(false);
  const [account, setAccount] = useState({
    full_name: '',
    email: '',
    password: '',
    confirm_password: '',
    persona: 'property_manager',
  });
  const [errors, setErrors] = useState<{ [k: string]: string }>({});
  const [submitting, setSubmitting] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [doneRoute, setDoneRoute] = useState('/onboarding');
  // The publishable key is fetched by <StripeCardPanel> (once per page load,
  // module-cached) rather than here, so this page no longer holds Stripe state.
  // signIn is deferred until the success "Done" click: flipping to 'authed' on
  // /signup makes RootRouter bounce us off the page. Cookies are already set.
  const [pendingAuth, setPendingAuth] = useState<PendingAuth | null>(null);
  const [successMeta, setSuccessMeta] = useState<{ promo: string | null }>({
    promo: null,
  });
  // FREE_SIGNUP, read from the server's public config (GET /stripe/config).
  //
  // PRESENTATION ONLY, and it matters that it is: POST /signup decides for
  // itself whether to contact Stripe, so a browser that flipped this value
  // would either be handed a SetupIntent it then has to confirm, or not. All
  // this controls is whether the wizard asks for a card.
  //
  // It starts FALSE and the payment step is the default, so a config request
  // that fails or is slow shows the paid wizard rather than briefly promising a
  // free account we would then have to charge for.
  const [freeSignup, setFreeSignup] = useState(false);

  useEffect(() => {
    let alive = true;
    publicApi
      .get('/staffing-models')
      .then(res => {
        if (!alive) return;
        const rows = Array.isArray(res.data) ? res.data : [];
        setStaffing(rows);
        // Default-select the first option (mockup has no empty "Select…").
        setBuilding(b => ({
          ...b,
          staffing_code: b.staffing_code || (rows[0] && rows[0].code) || '',
        }));
      })
      .catch(() => {
        /* staffing is optional */
      });

    // The same public endpoint <StripeCardPanel> reads for the publishable key.
    // Fetched here too rather than lifted out of that component: the panel's
    // cache is keyed to loading Stripe.js, which is exactly what must NOT
    // happen when there is no payment step.
    publicApi
      .get('/stripe/config')
      .then(res => {
        if (!alive) return;
        setFreeSignup(res.data?.free_signup === true);
        // A card step is coming: let Stripe load while the first three steps
        // are filled in. Not under free signup — there is no card step, and
        // then this page must not contact Stripe at all.
        if (res.data?.free_signup !== true) preloadStripe();
      })
      .catch(() => {
        /* unreachable config keeps the paid wizard — see the state above */
      });

    return () => {
      alive = false;
    };
  }, []);

  const setB =
    (name: keyof typeof building) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setBuilding(b => ({ ...b, [name]: e.target.value }));

  const setA =
    (name: keyof typeof account) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setAccount(a => ({ ...a, [name]: e.target.value }));

  const isCustom = !!plan && plan.contact_sales;
  const units = Math.max(0, parseInt(building.units, 10) || 0);
  const ratePerUnit = plan && !isCustom ? plan.price_cents / 100 : 0;
  const currency = plan && !isCustom ? plan.currency : 'usd';
  const interval: 'month' | 'year' =
    plan && plan.billing_interval === 'year' ? 'year' : 'month';
  // Rate x units for ONE billing period (a month, or a year on an annual
  // plan). No Math.max, no floor, no minimum — that is the whole of D-12.
  // Multiplied in INTEGER CENTS, exactly as Stripe multiplies unit_amount by
  // quantity, then converted once: dollars-times-units in floating point is
  // how a total drifts a cent from the invoice.
  const periodTotal = plan && !isCustom ? (plan.price_cents * units) / 100 : 0;
  const buildingLabel = `${building.name || '—'} · ${units} unit${
    units === 1 ? '' : 's'
  }`;

  const validateBuilding = (): boolean => {
    const next: { [k: string]: string } = {};
    if (!building.name.trim()) next.name = 'Add a building name to continue.';
    if (units < 1) next.units = 'Enter at least 1 unit.';
    if (!building.city.trim()) next.city = 'Enter a city.';
    if (pinUnresolved) {
      next.location = 'Pick a match from the list, or empty the box — this address is not saved until you do.';
    }
    setErrors(next);

    return Object.keys(next).length === 0;
  };

  const validateAccount = (): boolean => {
    const next: { [k: string]: string } = {};
    if (!account.full_name.trim()) next.full_name = 'Enter your name.';
    if (!EMAIL_RE.test(account.email)) next.email = 'Enter a valid email address.';
    if (account.password.length < 8) next.password = 'Use at least 8 characters.';
    if (!next.password && account.confirm_password !== account.password)
      next.confirm_password = 'Passwords do not match.';
    setErrors(next);

    return Object.keys(next).length === 0;
  };

  const emailIsTaken = async (): Promise<boolean> => {
    try {
      const res = await publicApi.get('/signup/email-available', {
        params: { email: account.email.trim() },
      });

      return res.data && res.data.available === false;
    } catch {
      return false;
    }
  };

  const refuse = () => setAttempt(n => n + 1);

  const goNext = async () => {
    setErrors({});
    setDirection('forward');
    if (step === 'plan') {
      if (!plan) {
        setErrors({ plan: 'Choose a plan to continue.' });
        refuse();

        return;
      }
      setStep('building');
    } else if (step === 'building') {
      if (validateBuilding()) setStep('account');
      else refuse();
    } else if (step === 'account') {
      if (!validateAccount()) {
        refuse();

        return;
      }
      setCheckingEmail(true);
      const taken = await emailIsTaken();
      setCheckingEmail(false);
      if (taken) {
        setErrors({ email: 'An account with this email already exists.' });
        refuse();

        return;
      }
      // FREE_SIGNUP: the account step IS the last step. There is no card to
      // collect, so this submits and goes straight to the success screen.
      //
      // `!isCustom` guards it, because the payment step is TWO screens in one:
      // the card form, and the contact-sales quote request. A custom plan still
      // needs that screen — it has no price to be free of.
      if (freeSignup && !isCustom) {
        await submitFreeSignup();

        return;
      }
      setStep('payment');
    }
  };

  const goBack = () => {
    setDirection('back');
    if (step === 'building') setStep('plan');
    else if (step === 'account') setStep('building');
    else if (step === 'payment') setStep('account');
  };

  // The API body. The single "Full name" is split on the first space so the
  // backend keeps first_name / last_name (last_name may be empty).
  const buildSignupPayload = (): Record<string, unknown> => {
    const name = account.full_name.trim();
    const sp = name.indexOf(' ');
    const first_name = sp === -1 ? name : name.slice(0, sp);
    const last_name = sp === -1 ? '' : name.slice(sp + 1).trim();

    return {
      first_name,
      last_name,
      email: account.email.trim(),
      password: account.password,
      persona: account.persona,
      plan_code: plan ? plan.code : undefined,
      // Sent so the server can REFUSE a disagreement (a monthly plan code with
      // an annual interval), never to choose the price — the code decides.
      billing_interval: plan && !plan.contact_sales ? plan.billing_interval : undefined,
      building: {
        name: building.name.trim(),
        units,
        city: building.city.trim(),
        staffing_code: building.staffing_code || null,
        /* THE BUILDING'S CLOCK.
           Desk times render in the BUILDING's zone, not the reader's. When a
           city was picked from the list its coordinates go along, and the
           server takes the zone from where that city IS; they are used for
           nothing else and are not stored.

           The browser's own zone is the fallback — for a typed city, or when
           no address provider is configured. Unvalidated here on purpose: the
           server ignores a zone it does not recognise (signup.js), so a browser
           reporting something odd costs the default rather than the signup. */
        city_latitude: cityPoint ? cityPoint.latitude : undefined,
        city_longitude: cityPoint ? cityPoint.longitude : undefined,
        timezone: deviceZone() || undefined,
        // The pin, when one was placed: the building's location, and the
        // zone the server takes ahead of the city's (signup.js, migration 139).
        location:
          pin.latitude !== null && pin.longitude !== null ? { ...pin } : undefined,
      },
    };
  };

  const onEmailTaken = () => {
    setErrors({ email: 'An account with this email already exists.' });
    setDirection('back');
    refuse();
    setStep('account');
  };

  const finishToSuccess = (
    pending: PendingAuth,
    route: string,
    promoDescription: string | null
  ) => {
    setPendingAuth(pending);
    setDoneRoute(route);
    setSuccessMeta({ promo: promoDescription });
    setDirection('forward');
    setStep('done');
  };

  // Custom (contact-sales) is a SALES LEAD, not a signup (D-17). It has no Stripe
  // price, so under "no account without Stripe" it cannot create one: the server
  // files the lead, notifies sales, and returns NO user and NO session. There is
  // nothing to sign in to, so `pendingAuth` stays null and "Done" goes to the
  // sign-in page rather than to an onboarding that does not exist.
  // FREE_SIGNUP — the whole signup, in one call, with no card step after it.
  //
  // The paid path splits this in two (prepareIntent creates the account and
  // returns a SetupIntent; onConfirmed finishes once Stripe has the card). With
  // no card there is nothing to confirm, so the 201 IS the end of the journey.
  //
  // `account_created` is asserted rather than assumed: the same route answers a
  // contact-sales request with 201 and NO account, and treating that as a
  // signup would sign the user in to something that does not exist.
  const submitFreeSignup = async () => {
    setSubmitting(true);
    try {
      const res = await publicApi.post('/signup', buildSignupPayload());
      const { user, onboarding, permissions, account_created } =
        res.data as SignupResponse;
      if (!user || account_created !== true) {
        throw new Error('Signup returned no account.');
      }
      finishToSuccess(
        {
          user,
          onboarding: onboarding || null,
          permissions: permissions || [],
        },
        resolvePostAuthRoute(user, onboarding || null, permissions || []),
        // No Stripe subscription means no coupon to attach. The server drops
        // any code silently for the same reason, so there is nothing to report.
        null
      );
    } catch (err) {
      const axiosError = err as AxiosError<{ message?: string }>;
      if (axiosError.response?.status === 409) {
        onEmailTaken();
      } else {
        toast.error(
          axiosError.response?.data?.message ||
            'Could not create your account. Please try again.'
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const submitCustom = async () => {
    setSubmitting(true);
    try {
      await publicApi.post('/signup', buildSignupPayload());
      setPendingAuth(null);
      setDoneRoute('/');
      setSuccessMeta({ promo: null });
      setDirection('forward');
      setStep('done');
    } catch (err) {
      const axiosError = err as AxiosError<{ message?: string }>;
      if (axiosError.response?.status === 409) {
        onEmailTaken();
      } else {
        toast.error(
          axiosError.response?.data?.message ||
            'Could not submit your request. Please try again.'
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const done = () => {
    if (pendingAuth) {
      signIn(pendingAuth.user, pendingAuth.onboarding, pendingAuth.permissions);
    }
    navigate(doneRoute);
  };

  const firstName = account.full_name.trim().split(' ')[0] || 'there';
  // Three steps instead of four when there is no card to collect — the progress
  // bar must not promise a step the user will never reach. A custom plan keeps
  // the fourth: its "payment" step is the quote request.
  const order = freeSignup && !isCustom ? FREE_ORDER : ORDER;
  const stepIndex = step === 'done' ? order.length : order.indexOf(step) + 1;

  return (
    <Shell>
      <Card>
        <BrandRow>
          <LogoMark size={40} aria-hidden="true" />
          <Wordmark>Lobbero</Wordmark>
        </BrandRow>

        {/* ONE CONTINUOUS CARD. The progress bar and the footer stay mounted
            across every step (they collapse, they are never remounted), the
            step slides in the direction of travel, and AutoHeight tweens the
            card between step heights instead of letting it snap. */}
        <AutoHeight>
          <Collapse open={step !== 'done'}>
            <OnboardingProgress current={stepIndex} total={order.length} />
          </Collapse>

          <StepTransition stepKey={step} direction={direction} style={STEP_LAYER_STYLE}>
            {step === 'plan' && (
              <>
                <Heading>Choose your plan</Heading>
                {/* The trial promise is the PAID wizard's. Under FREE_SIGNUP
                    there is no trial to start and no card to end it, so saying
                    "30-day free trial" would set a clock that does not exist
                    and imply a charge that is not coming. */}
                <Subheading>
                  {freeSignup && !isCustom
                    ? 'Choose the plan you want — no card required while we are in early access. You can switch plans anytime.'
                    : 'Every plan starts with a 30-day free trial. You can switch plans anytime.'}
                </Subheading>
                <PlanPicker
                  selectedId={plan?.id ?? null}
                  onSelect={setPlan}
                  preferCode={requestedPlanCode}
                  preferInterval={requestedInterval}
                />
                <FieldError message={errors.plan} />
                <FullWidthButton type="button" onClick={goNext}>
                  Continue <RightArrow />
                </FullWidthButton>
              </>
            )}

            {step === 'building' && (
              <>
                <Heading>About your building</Heading>
                <Subheading>
                  This sets up your workspace — you can add more buildings later.
                </Subheading>

                <Field>
                  <Label htmlFor="b-name">Building name</Label>
                  <Shake active={!!errors.name} attempt={attempt}>
                    <Input
                      id="b-name"
                      value={building.name}
                      onChange={setB('name')}
                      hasError={!!errors.name}
                      placeholder="e.g. Marina Tower"
                    />
                  </Shake>
                  <FieldError message={errors.name} />
                </Field>

                <FieldRow>
                  <Field>
                    <Label htmlFor="b-units">Number of units</Label>
                    <Shake active={!!errors.units} attempt={attempt}>
                      <Input
                        id="b-units"
                        type="number"
                        min={1}
                        value={building.units}
                        onChange={setB('units')}
                        hasError={!!errors.units}
                        placeholder="150"
                      />
                    </Shake>
                    <FieldError message={errors.units} />
                    {/* WHAT THE NUMBER COSTS, BESIDE THE NUMBER. Pricing is
                        per unit, so this field sets the bill; it used to be
                        the one input whose consequence was three steps away.
                        Hidden on a custom plan, where there is no public
                        rate to multiply. */}
                    {units > 0 && !isCustom && ratePerUnit > 0 ? (
                      <FieldHint>
                        {units} × {money2(ratePerUnit, currency)} ={' '}
                        <strong>{money2(periodTotal, currency)}</strong>
                        {interval === 'year' ? ' per year' : ' per month'}
                      </FieldHint>
                    ) : null}
                  </Field>
                  <Field>
                    <Label htmlFor="b-city">City</Label>
                    {/* Same single field the mockup has — CityAutocomplete only
                      adds city suggestions behind it. With no address provider,
                      or if the lookup fails, it is an ordinary text input. */}
                    <Shake active={!!errors.city} attempt={attempt}>
                      <CityAutocomplete
                        id="b-city"
                        value={building.city}
                        onChange={city => setBuilding(current => ({ ...current, city }))}
                        onPick={setCityPoint}
                        hasError={!!errors.city}
                        placeholder="Lisbon"
                        askForLocation={false}
                      />
                    </Shake>
                    <FieldError message={errors.city} />
                  </Field>
                </FieldRow>

                {/* WHERE IT IS — the map, signed out (geo-access.tsx). The
                    pin fills the city when the city box is still empty. */}
                <Field data-signup-location>
                  <FieldHint>
                    Optional: place the building on the map. Its address and time zone
                    come from the pin.
                  </FieldHint>
                  <GeoAccessContext.Provider value={PUBLIC_GEO}>
                    <BuildingLocationPicker
                      value={pin}
                      onChange={next => {
                        setPin(next);
                        if (next.city) {
                          setBuilding(current =>
                            current.city.trim() ? current : { ...current, city: next.city || '' }
                          );
                        }
                      }}
                      onUnresolvedChange={setPinUnresolved}
                      invalid={!!errors.location}
                      useLocationWhenGranted={false}
                      manualFields={false}
                    />
                  </GeoAccessContext.Provider>
                  <FieldError message={errors.location} />
                </Field>

                <Field>
                  <Label htmlFor="b-staff">Front desk staffing</Label>
                  <Select
                    id="b-staff"
                    value={building.staffing_code}
                    onChange={setB('staffing_code')}
                  >
                    {staffing.map(s => (
                      <option key={s.code} value={s.code}>
                        {s.label}
                      </option>
                    ))}
                  </Select>
                </Field>

                <ButtonRow>
                  <SecondaryButton type="button" onClick={goBack}>
                    <LeftArrow /> Back
                  </SecondaryButton>
                  <PrimaryButton type="button" onClick={goNext}>
                    Continue <RightArrow />
                  </PrimaryButton>
                </ButtonRow>
              </>
            )}

            {step === 'account' && (
              <>
                <Heading>Create your account</Heading>
                <Subheading>
                  You&rsquo;ll be the workspace owner and can invite your team next.
                </Subheading>

                <FieldRow>
                  <Field>
                    <Label htmlFor="a-name">Full name</Label>
                    <Shake active={!!errors.full_name} attempt={attempt}>
                      <Input
                        id="a-name"
                        value={account.full_name}
                        onChange={setA('full_name')}
                        hasError={!!errors.full_name}
                        placeholder="Ana Ferreira"
                        autoComplete="name"
                      />
                    </Shake>
                    <FieldError message={errors.full_name} />
                  </Field>
                  <Field>
                    <Label htmlFor="a-role">Your role</Label>
                    <Select
                      id="a-role"
                      value={account.persona}
                      onChange={setA('persona')}
                    >
                      {PERSONAS.map(p => (
                        <option key={p.value} value={p.value}>
                          {p.label}
                        </option>
                      ))}
                    </Select>
                  </Field>
                </FieldRow>

                <Field>
                  <Label htmlFor="a-email">Work email</Label>
                  <Shake active={!!errors.email} attempt={attempt}>
                    <Input
                      id="a-email"
                      type="email"
                      value={account.email}
                      onChange={setA('email')}
                      hasError={!!errors.email}
                      placeholder="ana@lobbero.com"
                      autoComplete="email"
                    />
                  </Shake>
                  <FieldError message={errors.email} />
                </Field>

                <Field>
                  <Label htmlFor="a-pass">Password</Label>
                  <Shake active={!!errors.password} attempt={attempt}>
                    <Input
                      id="a-pass"
                      type="password"
                      value={account.password}
                      onChange={setA('password')}
                      hasError={!!errors.password}
                      placeholder="8+ characters"
                      autoComplete="new-password"
                    />
                  </Shake>
                  <FieldError message={errors.password} />
                </Field>

                <Field>
                  <Label htmlFor="a-confirm">Confirm password</Label>
                  <Shake active={!!errors.confirm_password} attempt={attempt}>
                    <Input
                      id="a-confirm"
                      type="password"
                      value={account.confirm_password}
                      onChange={setA('confirm_password')}
                      hasError={!!errors.confirm_password}
                      placeholder="Re-enter your password"
                      autoComplete="new-password"
                    />
                  </Shake>
                  <FieldError message={errors.confirm_password} />
                </Field>

                <ButtonRow>
                  <SecondaryButton
                    type="button"
                    onClick={goBack}
                    disabled={checkingEmail || submitting}
                  >
                    <LeftArrow /> Back
                  </SecondaryButton>
                  <PrimaryButton
                    type="button"
                    onClick={goNext}
                    disabled={checkingEmail || submitting}
                  >
                    {/* With no payment step after it, this button is what
                        creates the account — so it says so, and it carries the
                        submitting state the payment step would otherwise own.
                        StatusLabel sizes itself to the WIDEST label it is
                        given, so each case passes only its own pair rather
                        than one merged set that would leave a gap. */}
                    {freeSignup && !isCustom ? (
                      <StatusLabel
                        state={checkingEmail || submitting ? 'busy' : 'idle'}
                        labels={{ idle: 'Create account', busy: 'Creating…' }}
                      />
                    ) : (
                      <StatusLabel
                        state={checkingEmail ? 'busy' : 'idle'}
                        labels={{ idle: 'Continue', busy: 'Checking…' }}
                      />
                    )}{' '}
                    <RightArrow />
                  </PrimaryButton>
                </ButtonRow>
              </>
            )}

            {step === 'payment' && (
              <>
                <Heading>
                  {isCustom ? 'Request your quote' : 'Start your free trial'}
                </Heading>
                <Subheading>
                  {isCustom
                    ? 'No card needed — tell us about your portfolio and our team will follow up with pricing.'
                    : interval === 'year'
                      ? 'Nothing is charged for 30 days, then you are billed once a year. Cancel anytime before the trial ends.'
                      : 'Nothing is charged for 30 days. Cancel anytime before the trial ends.'}
                </Subheading>

                {isCustom ? (
                  <>
                    <SummaryBox>
                      <SummaryRow>
                        <span>Plan</span>
                        <SummaryValue>{plan?.name}</SummaryValue>
                      </SummaryRow>
                      <SummaryRow>
                        <span>Building</span>
                        <SummaryValue>{buildingLabel}</SummaryValue>
                      </SummaryRow>
                      <SummaryTotal>
                        <span>Next step</span>
                        <span>We&rsquo;ll email you a quote</span>
                      </SummaryTotal>
                    </SummaryBox>
                    <ButtonRow>
                      <SecondaryButton
                        type="button"
                        onClick={goBack}
                        disabled={submitting}
                      >
                        <LeftArrow /> Back
                      </SecondaryButton>
                      <PrimaryButton
                        type="button"
                        onClick={submitCustom}
                        disabled={submitting}
                      >
                        <StatusLabel
                          state={submitting ? 'busy' : 'idle'}
                          labels={{ idle: 'Request a quote', busy: 'Sending…' }}
                        />{' '}
                        <RightArrow />
                      </PrimaryButton>
                    </ButtonRow>
                  </>
                ) : (
                  <TrialPaymentForm
                    planName={plan?.name ?? ''}
                    planCode={plan?.code ?? ''}
                    buildingLabel={buildingLabel}
                    units={units}
                    ratePerUnit={ratePerUnit}
                    periodTotal={periodTotal}
                    interval={interval}
                    currency={currency}
                    customerName={account.full_name.trim()}
                    email={account.email.trim()}
                    buildPayload={buildSignupPayload}
                    onBack={goBack}
                    onEmailTaken={onEmailTaken}
                    onComplete={finishToSuccess}
                  />
                )}
              </>
            )}

            {step === 'done' && (
              /* THE LANDING. The tick pops with one overshoot; the heading, the
               copy and the button follow in a short cascade rather than
               arriving as one block. */
              <SuccessBlock>
                <Pop as="div">
                  <SuccessIcon aria-hidden="true">✓</SuccessIcon>
                </Pop>
                <SuccessRow {...arrive(0)}>
                  <Heading>
                    {isCustom
                      ? `Thanks, ${firstName}.`
                      : `Welcome to Lobbero, ${firstName}.`}
                  </Heading>
                </SuccessRow>
                {/* Custom created a sales lead, NOT an account (D-17) — so this
                  must not promise a workspace, a trial or a sign-in link. */}
                <SuccessRow {...arrive(1)}>
                  <Subheading>
                    {isCustom
                      ? `Your request is with our team and we'll email pricing to ${account.email}. Nothing has been set up yet — we'll build your workspace with you once the quote is agreed.`
                      : freeSignup
                        ? // No card, so no trial clock either: promising one
                          // would start a countdown nothing is going to honour.
                          `Your workspace is ready. Check your email for the sign-in link, then invite your front desk.`
                        : `${
                            successMeta.promo ? `${successMeta.promo} applied. ` : ''
                          }Your workspace is ready and your 30-day trial has started. Check your email for the sign-in link, then invite your front desk.`}
                  </Subheading>
                </SuccessRow>
                <SuccessRow {...arrive(2)}>
                  <FullWidthButton type="button" onClick={done}>
                    Done
                  </FullWidthButton>
                </SuccessRow>
              </SuccessBlock>
            )}
          </StepTransition>

          <Collapse open={step !== 'done'}>
            <Collapse open={step === 'payment' && !isCustom}>
              <HelperText>
                Payment is processed securely by Stripe — we never see your card.
              </HelperText>
            </Collapse>
            {/* THE TERMS THE CARD IS BEING GIVEN AGAINST.
                This wizard collects a full name, a work email, a building name,
                a city, a unit count and a credit card, and carried NO link to
                terms, NO link to a privacy policy and no statement of what
                happens to any of it. For a paid B2B subscription that is both a
                trust problem at the exact moment trust is being asked for, and
                an obligation in the EU and the UK.

                The app now carries its own copy of every policy at
                /legal/<doc> (synced from lobbero_web by
                scripts/sync-legal.mjs). New tab, so a half-filled form is not
                lost to reading the terms. */}
            <HelperText>
              By continuing you agree to our{' '}
              <a href="/legal/terms" target="_blank" rel="noreferrer">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="/legal/privacy" target="_blank" rel="noreferrer">
                Privacy Policy
              </a>
              . We use your details to run your building&apos;s account and never sell
              them.
            </HelperText>
            <TopLink>
              Already have an account? <Link to="/">Sign in</Link>
            </TopLink>
          </Collapse>
        </AutoHeight>
      </Card>
    </Shell>
  );
};
